import React from 'react';
import { usePage } from '@inertiajs/react';
import { LogIn, Clock, DollarSign, Repeat, Cookie, Wallet, AlertTriangle, XCircle } from 'lucide-react';
import { PublicLayout } from '../../design-system/components/layouts';
import { Button, Card, CardBody, Badge } from '../../design-system/components';

function SignInForm({ inviteToken, children }) {
  return (
    <form action="/auth/speakflow" method="post">
      <input
        type="hidden"
        name="authenticity_token"
        value={document.querySelector('meta[name="csrf-token"]')?.content ?? ''}
      />
      <input type="hidden" name="invite_token" value={inviteToken} />
      {children}
    </form>
  );
}

function ErrorState({ error }) {
  const config = {
    not_found: {
      icon: XCircle,
      title: 'Invite not found',
      description: 'This invite link is invalid or has been removed.',
    },
    expired: {
      icon: Clock,
      title: 'Invite expired',
      description: 'This invite link has expired and is no longer accepting new signups.',
    },
    exhausted: {
      icon: AlertTriangle,
      title: 'Invite limit reached',
      description: 'This invite link has reached its maximum number of uses.',
    },
    inactive: {
      icon: XCircle,
      title: 'Invite inactive',
      description: 'This invite link has been deactivated.',
    },
  };

  const { icon: Icon, title, description } = config[error] || config.not_found;

  return (
    <section className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md text-center py-20">
        <div className="mx-auto w-12 h-12 rounded-full bg-surface-danger-light text-fg-danger flex items-center justify-center">
          <Icon size={24} strokeWidth={1.5} />
        </div>
        <h1 className="mt-4 text-2xl font-bold text-fg">{title}</h1>
        <p className="mt-2 text-fg-secondary">{description}</p>
      </div>
    </section>
  );
}

const detailItems = [
  { key: 'commission_display', label: 'Commission', icon: DollarSign },
  { key: 'duration_display', label: 'Duration', icon: Repeat },
  { key: 'cookie_window_days', label: 'Cookie Window', icon: Cookie, suffix: ' days' },
  { key: 'min_payout_display', label: 'Min. Payout', icon: Wallet },
];

export default function Invite() {
  const { props } = usePage();
  const { error, invite, campaign } = props;
  const appName = props.app_config?.company_name ?? 'Speakflow';

  if (error) {
    return (
      <PublicLayout>
        <ErrorState error={error} />
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <section className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-lg py-16 sm:py-24">
          <div className="text-center">
            <Badge variant="success" size="sm">
              Invite
            </Badge>
            <h1 className="mt-4 text-3xl sm:text-4xl font-bold text-fg tracking-tight">
              Join {campaign.name}
            </h1>
            <p className="mt-3 text-fg-secondary">
              You've been invited to join the {appName} affiliate program. Sign in to get started.
            </p>
          </div>

          <Card className="mt-8">
            <CardBody>
              <h2 className="text-sm font-semibold text-fg-secondary uppercase tracking-wider mb-4">
                Campaign Details
              </h2>
              <dl className="space-y-3">
                {detailItems.map(({ key, label, icon: Icon, suffix }) => {
                  const value = campaign[key];
                  if (!value) return null;
                  return (
                    <div key={key} className="flex items-center justify-between">
                      <dt className="flex items-center gap-2 text-sm text-fg-secondary">
                        <Icon size={16} strokeWidth={1.5} className="text-fg-tertiary" />
                        {label}
                      </dt>
                      <dd className="text-sm font-medium text-fg">
                        {value}{suffix || ''}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            </CardBody>
          </Card>

          <div className="mt-6 text-center">
            <SignInForm inviteToken={invite.token}>
              <Button type="submit" size="lg" className="w-full text-base">
                <LogIn size={20} />
                Join Campaign
              </Button>
            </SignInForm>
            <p className="mt-3 text-xs text-fg-tertiary">
              Sign in with your {appName} account to join. Don't have an account?{' '}
              <a
                href={props.app_config?.product_url ?? '#'}
                className="text-fg font-medium underline underline-offset-2 hover:text-fg-secondary transition-colors"
              >
                Sign up
              </a>
            </p>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
